import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import GTranslateRoundedIcon from '@mui/icons-material/GTranslateRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import AdsClickRoundedIcon from '@mui/icons-material/AdsClickRounded';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import Dashboard from '~/features/dashboard/Dashboard';
import { DataTable } from '~/shared/ui/tables/DataTable';
import { defaultPaginationModel } from '~/shared/const/app';
import { hasAccess } from '~/shared/config/access';
import { useSelector } from 'react-redux';
import { usersApi } from '~/features/user/model/api/users-api';
import {
  CtaDictionaryItemDto,
  CtaLanguageDefinition,
  useGetCtaDictionaryAdminOptionsQuery,
} from '~/features/dictionaries/model/api/cta-dictionary-api';
import { CtaDictionaryModal } from '~/shared/ui/modals/CtaDictionaryModal';
import { IFilterConfig } from '~/shared/types/types';
import { CtaLanguageModal } from '~/shared/ui/modals/CtaLanguageModal';
import { CtaTranslationModal } from '~/shared/ui/modals/CtaTranslationModal';

type DictionaryTab = 'translations' | 'languages' | 'cta';

type DictionaryRow = Record<string, any>;

const matchesText = (value: string, search: string) =>
  value.toLowerCase().includes(search.toLowerCase());

const normalizeListFilter = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split('\n').map(item => item.trim()).filter(Boolean);
  }

  return [];
};

const matchesBooleanFilter = (value: boolean, filterValue: unknown) => {
  if (filterValue === '' || filterValue === null || typeof filterValue === 'undefined') {
    return true;
  }

  if (filterValue === 'true' || filterValue === true) {
    return value === true;
  }

  if (filterValue === 'false' || filterValue === false) {
    return value === false;
  }

  return true;
};

const sortRows = (rows: DictionaryRow[], sortModel: any[]) => {
  if (!sortModel?.length) {
    return rows;
  }

  const [{ field, sort }] = sortModel;
  if (!field || !sort) {
    return rows;
  }

  const direction = sort === 'desc' ? -1 : 1;

  return [...rows].sort((left, right) => {
    const leftValue = left[field];
    const rightValue = right[field];

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
      return (Number(leftValue) - Number(rightValue)) * direction;
    }

    return String(leftValue ?? '').localeCompare(String(rightValue ?? ''), 'ru') * direction;
  });
};

const sliceRows = (rows: DictionaryRow[], paginationModel: { page: number; pageSize: number }) => {
  const start = paginationModel.page * paginationModel.pageSize;
  return rows.slice(start, start + paginationModel.pageSize);
};

export const CtaDictionaryPage = () => {
  const { data: me } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;
  const [openCtaModal, setOpenCtaModal] = useState(false);
  const [openLanguageModal, setOpenLanguageModal] = useState(false);
  const [openTranslationModal, setOpenTranslationModal] = useState(false);
  const [editingItem, setEditingItem] = useState<CtaDictionaryItemDto | null>(null);
  const [editingLanguage, setEditingLanguage] = useState<CtaLanguageDefinition | null>(null);
  const [editingTranslation, setEditingTranslation] = useState<Record<string, any> | null>(null);
  const [activeTab, setActiveTab] = useState<DictionaryTab>('translations');
  const [ctaSelectedIds, setCtaSelectedIds] = useState<any[]>([]);
  const [languageSelectedIds, setLanguageSelectedIds] = useState<any[]>([]);
  const [translationSelectedIds, setTranslationSelectedIds] = useState<any[]>([]);

  const [ctaFilters, setCtaFilters] = useState<Record<string, any> | null>(null);
  const [languageFilters, setLanguageFilters] = useState<Record<string, any> | null>(null);
  const [translationFilters, setTranslationFilters] = useState<Record<string, any> | null>(null);

  const [ctaSortModel, setCtaSortModel] = useState<any[]>([{ field: 'sort', sort: 'asc' }]);
  const [languageSortModel, setLanguageSortModel] = useState<any[]>([{ field: 'sort', sort: 'asc' }]);
  const [translationSortModel, setTranslationSortModel] = useState<any[]>([
    { field: 'language_code', sort: 'asc' },
  ]);

  const [ctaPaginationModel, setCtaPaginationModel] = useState({ ...defaultPaginationModel });
  const [languagePaginationModel, setLanguagePaginationModel] = useState({ ...defaultPaginationModel });
  const [translationPaginationModel, setTranslationPaginationModel] = useState({ ...defaultPaginationModel });

  const { data: adminOptions, isFetching, refetch } = useGetCtaDictionaryAdminOptionsQuery();

  const ctaItems = adminOptions?.data?.items || [];
  const languages = adminOptions?.data?.languages || [];
  const defaultLanguageCode = adminOptions?.data?.default_language_code
    || languages.find(item => item.is_default)?.code
    || 'en-US';

  const ctaFilterColumns: IFilterConfig[] = useMemo(() => [
    { key: 'key', label: 'CTA ключ', type: 'text' },
    { key: 'english_text', label: 'CTA', type: 'text' },
    {
      key: 'is_active',
      label: 'Статус',
      type: 'select',
      options: [
        { label: 'Активен', value: 'true' },
        { label: 'Выключен', value: 'false' },
      ],
    },
  ], []);

  const languageFilterColumns: IFilterConfig[] = useMemo(() => [
    { key: 'code', label: 'Код', type: 'text' },
    { key: 'name', label: 'Название', type: 'text' },
    {
      key: 'is_default',
      label: 'По умолчанию',
      type: 'select',
      options: [
        { label: 'Да', value: 'true' },
        { label: 'Нет', value: 'false' },
      ],
    },
    {
      key: 'is_active',
      label: 'Активен',
      type: 'select',
      options: [
        { label: 'Активен', value: 'true' },
        { label: 'Выключен', value: 'false' },
      ],
    },
  ], []);

  const translationFilterColumns: IFilterConfig[] = useMemo(() => [
    { key: 'language_code', label: 'Код языка', type: 'text' },
    { key: 'cta_key', label: 'CTA ключ', type: 'text' },
    { key: 'translation', label: 'Перевод', type: 'text' },
    {
      key: 'is_active',
      label: 'Активен',
      type: 'select',
      options: [
        { label: 'Активен', value: 'true' },
        { label: 'Выключен', value: 'false' },
      ],
    },
  ], []);

  const ctaRows = useMemo(() => {
    return ctaItems.map((item, index) => ({
      ...item,
      id: item.id ?? index + 1,
      english_text: item.texts?.[defaultLanguageCode] || '',
    }));
  }, [ctaItems, defaultLanguageCode]);

  const languageRows = useMemo(() => {
    return languages.map((language, index) => ({
      id: language.id ?? index + 1,
      code: language.code,
      name: language.name ?? language.label,
      label: language.label,
      native_label: language.native_label,
      sort: language.sort ?? 100,
      is_default: language.is_default,
      is_active: language.is_active ?? true,
    }));
  }, [languages]);

  const translationRows = useMemo(() => {
    let id = 1;
    return ctaRows.flatMap(item =>
      languageRows.map(language => ({
        id: id++,
        dictionary_item_id: item.id,
        language_code: language.code,
        language_name: language.name,
        cta_key: item.key,
        cta: item.texts?.[defaultLanguageCode] || '',
        translation: item.texts?.[language.code] || item.texts?.[defaultLanguageCode] || '',
        is_active: item.is_active && language.is_active,
        updated_at: item.updated_at,
        source_item: item,
      })),
    );
  }, [ctaRows, languageRows, defaultLanguageCode]);

  const filteredCtaRows = useMemo(() => {
    const search = String(ctaPaginationModel.search || '').trim();
    return ctaRows.filter(row => {
      if (search && ![row.key, row.english_text].some(value => matchesText(String(value || ''), search))) {
        return false;
      }

      const keys = normalizeListFilter(ctaFilters?.key);
      if (keys.length > 0 && !keys.some(key => matchesText(String(row.key || ''), key))) {
        return false;
      }

      const ctaTexts = normalizeListFilter(ctaFilters?.english_text);
      if (ctaTexts.length > 0 && !ctaTexts.some(value => matchesText(String(row.english_text || ''), value))) {
        return false;
      }

      return matchesBooleanFilter(Boolean(row.is_active), ctaFilters?.is_active);
    });
  }, [ctaRows, ctaFilters, ctaPaginationModel.search]);

  const filteredLanguageRows = useMemo(() => {
    const search = String(languagePaginationModel.search || '').trim();
    return languageRows.filter(row => {
      if (search && ![row.code, row.name, row.native_label].some(value => matchesText(String(value || ''), search))) {
        return false;
      }

      const codes = normalizeListFilter(languageFilters?.code);
      if (codes.length > 0 && !codes.some(code => matchesText(String(row.code || ''), code))) {
        return false;
      }

      const names = normalizeListFilter(languageFilters?.name);
      if (names.length > 0 && !names.some(name => matchesText(String(row.name || ''), name))) {
        return false;
      }

      if (!matchesBooleanFilter(Boolean(row.is_default), languageFilters?.is_default)) {
        return false;
      }

      return matchesBooleanFilter(Boolean(row.is_active), languageFilters?.is_active);
    });
  }, [languageRows, languageFilters, languagePaginationModel.search]);

  const filteredTranslationRows = useMemo(() => {
    const search = String(translationPaginationModel.search || '').trim();
    return translationRows.filter(row => {
      if (search && ![row.language_code, row.language_name, row.cta_key, row.cta, row.translation].some(
        value => matchesText(String(value || ''), search),
      )) {
        return false;
      }

      const languageCodes = normalizeListFilter(translationFilters?.language_code);
      if (languageCodes.length > 0 && !languageCodes.some(code => matchesText(String(row.language_code || ''), code))) {
        return false;
      }

      const ctaKeys = normalizeListFilter(translationFilters?.cta_key);
      if (ctaKeys.length > 0 && !ctaKeys.some(key => matchesText(String(row.cta_key || ''), key))) {
        return false;
      }

      const translations = normalizeListFilter(translationFilters?.translation);
      if (translations.length > 0 && !translations.some(value => matchesText(String(row.translation || ''), value))) {
        return false;
      }

      return matchesBooleanFilter(Boolean(row.is_active), translationFilters?.is_active);
    });
  }, [translationRows, translationFilters, translationPaginationModel.search]);

  const pagedCtaRows = useMemo(
    () => sliceRows(sortRows(filteredCtaRows, ctaSortModel), ctaPaginationModel),
    [filteredCtaRows, ctaSortModel, ctaPaginationModel],
  );
  const pagedLanguageRows = useMemo(
    () => sliceRows(sortRows(filteredLanguageRows, languageSortModel), languagePaginationModel),
    [filteredLanguageRows, languageSortModel, languagePaginationModel],
  );
  const pagedTranslationRows = useMemo(
    () => sliceRows(sortRows(filteredTranslationRows, translationSortModel), translationPaginationModel),
    [filteredTranslationRows, translationSortModel, translationPaginationModel],
  );

  const ctaColumns: GridColDef[] = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 70, flex: 0 },
    { field: 'key', headerName: 'CTA ключ', minWidth: 180, flex: 0 },
    { field: 'english_text', headerName: 'CTA', minWidth: 220, flex: 0 },
    {
      field: 'is_active',
      headerName: 'Активен',
      width: 120,
      flex: 0,
      renderCell: params => params.value
        ? <Chip size="small" color="success" variant="outlined" label="Активен" />
        : <Chip size="small" color="default" variant="outlined" label="Выключен" />,
    },
    { field: 'sort', headerName: 'Сортировка', width: 120, flex: 0 },
    {
      field: 'updated_at',
      headerName: 'Обновлен',
      width: 180,
      flex: 0,
      renderCell: params => <span>{params.value ? new Date(params.value).toLocaleString('ru-RU') : ''}</span>,
    },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Tooltip title="Редактировать CTA">
          <IconButton
            size="small"
            onClick={() => {
              setEditingItem(params.row);
              setOpenCtaModal(true);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  const languagesColumns: GridColDef[] = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 70, flex: 0 },
    { field: 'code', headerName: 'Код', width: 120, flex: 0 },
    { field: 'name', headerName: 'Название', minWidth: 200, flex: 0 },
    { field: 'native_label', headerName: 'Native label', minWidth: 220, flex: 0 },
    { field: 'sort', headerName: 'Сортировка', width: 120, flex: 0 },
    {
      field: 'is_default',
      headerName: 'По умолчанию',
      width: 140,
      flex: 0,
      renderCell: params => params.value
        ? <Chip size="small" color="success" label="Да" />
        : <Chip size="small" variant="outlined" label="Нет" />,
    },
    {
      field: 'is_active',
      headerName: 'Активен',
      width: 120,
      flex: 0,
      renderCell: params => params.value
        ? <Chip size="small" color="success" variant="outlined" label="Активен" />
        : <Chip size="small" color="default" variant="outlined" label="Выключен" />,
    },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Tooltip title="Редактировать язык">
          <IconButton
            size="small"
            onClick={() => {
              setEditingLanguage(params.row);
              setOpenLanguageModal(true);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  const translationsColumns: GridColDef[] = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 70, flex: 0 },
    { field: 'language_code', headerName: 'Код языка', width: 140, flex: 0 },
    { field: 'language_name', headerName: 'Язык', minWidth: 180, flex: 0 },
    { field: 'cta_key', headerName: 'CTA ключ', minWidth: 160, flex: 0 },
    { field: 'cta', headerName: 'CTA', minWidth: 160, flex: 0 },
    { field: 'translation', headerName: 'Перевод', minWidth: 220, flex: 0 },
    {
      field: 'is_active',
      headerName: 'Активен',
      width: 120,
      flex: 0,
      renderCell: params => params.value
        ? <Chip size="small" color="success" variant="outlined" label="Активен" />
        : <Chip size="small" color="default" variant="outlined" label="Выключен" />,
    },
    {
      field: 'updated_at',
      headerName: 'Обновлен',
      width: 180,
      flex: 0,
      renderCell: params => <span>{params.value ? new Date(params.value).toLocaleString('ru-RU') : ''}</span>,
    },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Tooltip title="Редактировать перевод">
          <IconButton
            size="small"
            onClick={() => {
              setEditingTranslation(params.row);
              setOpenTranslationModal(true);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  const tabCards = [
    {
      key: 'translations',
      label: 'Переводы',
      icon: <TranslateRoundedIcon fontSize="small" />,
      count: filteredTranslationRows.length,
    },
    {
      key: 'languages',
      label: 'Языки',
      icon: <GTranslateRoundedIcon fontSize="small" />,
      count: filteredLanguageRows.length,
    },
    {
      key: 'cta',
      label: 'CTA',
      icon: <AdsClickRoundedIcon fontSize="small" />,
      count: filteredCtaRows.length,
    },
  ] as const;

  if (!hasAccess(me?.roles ?? [], '/dictionaries/cta')) {
    return (
      <Dashboard>
        <Stack>Нет доступа</Stack>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <Stack gap={2}>
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Stack spacing={2} p={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography component="h1" variant="h5" fontWeight={700}>
                  Справочник CTA
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Управление языками, CTA-ключами и переводами без изменения текущего процесса создания РК.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${languageRows.length} языков`} color="primary" variant="outlined" />
                <Chip label={`${ctaRows.length} CTA`} color="primary" variant="outlined" />
                <Chip label={`${translationRows.length} переводов`} color="primary" variant="outlined" />
              </Stack>
            </Stack>

            <Tabs
              value={activeTab}
              onChange={(_e, value) => setActiveTab(value)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {tabCards.map(tab => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  icon={tab.icon}
                  iconPosition="start"
                  label={`${tab.label} (${tab.count})`}
                />
              ))}
            </Tabs>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Stack spacing={2} p={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5}>
              <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
                {activeTab === 'translations' ? 'Переводы CTA' : activeTab === 'languages' ? 'Языки CTA' : 'CTA'}
              </Typography>

              {activeTab === 'cta' && (
                <Button
                  variant="contained"
                  startIcon={<AddBoxIcon />}
                  onClick={() => {
                    setEditingItem(null);
                    setOpenCtaModal(true);
                  }}
                >
                  Добавить CTA
                </Button>
              )}

              {activeTab === 'languages' && (
                <Button
                  variant="contained"
                  startIcon={<AddBoxIcon />}
                  onClick={() => {
                    setEditingLanguage(null);
                    setOpenLanguageModal(true);
                  }}
                >
                  Добавить язык
                </Button>
              )}
            </Stack>

            {activeTab === 'cta' && (
              <DataTable
                rows={pagedCtaRows}
                columns={ctaColumns}
                rowCount={filteredCtaRows.length}
                isFetching={isFetching}
                paginationModel={ctaPaginationModel}
                setPaginationModel={setCtaPaginationModel}
                selectedIds={ctaSelectedIds}
                setSelectedIds={setCtaSelectedIds}
                sortModel={ctaSortModel}
                setSortModel={(model: any) => {
                  setCtaSortModel(model);
                  setCtaPaginationModel((prev: any) => ({ ...prev, page: 0 }));
                }}
                sortingMode="server"
                filterColumns={ctaFilterColumns}
                onFilterApply={(filterState: Record<string, any>) => {
                  setCtaFilters(filterState);
                  setCtaPaginationModel((prev: any) => ({ ...prev, page: 0 }));
                }}
                onFilterReset={() => {
                  setCtaFilters(null);
                  setCtaPaginationModel((prev: any) => ({ ...prev, page: 0, search: '' }));
                }}
                activeFilters={ctaFilters}
                onRefresh={refetch}
                checkboxSelection={false}
              />
            )}

            {activeTab === 'languages' && (
              <DataTable
                rows={pagedLanguageRows}
                columns={languagesColumns}
                rowCount={filteredLanguageRows.length}
                isFetching={isFetching}
                paginationModel={languagePaginationModel}
                setPaginationModel={setLanguagePaginationModel}
                selectedIds={languageSelectedIds}
                setSelectedIds={setLanguageSelectedIds}
                sortModel={languageSortModel}
                setSortModel={(model: any) => {
                  setLanguageSortModel(model);
                  setLanguagePaginationModel((prev: any) => ({ ...prev, page: 0 }));
                }}
                sortingMode="server"
                filterColumns={languageFilterColumns}
                onFilterApply={(filterState: Record<string, any>) => {
                  setLanguageFilters(filterState);
                  setLanguagePaginationModel((prev: any) => ({ ...prev, page: 0 }));
                }}
                onFilterReset={() => {
                  setLanguageFilters(null);
                  setLanguagePaginationModel((prev: any) => ({ ...prev, page: 0, search: '' }));
                }}
                activeFilters={languageFilters}
                onRefresh={refetch}
                checkboxSelection={false}
              />
            )}

            {activeTab === 'translations' && (
              <DataTable
                rows={pagedTranslationRows}
                columns={translationsColumns}
                rowCount={filteredTranslationRows.length}
                isFetching={isFetching}
                paginationModel={translationPaginationModel}
                setPaginationModel={setTranslationPaginationModel}
                selectedIds={translationSelectedIds}
                setSelectedIds={setTranslationSelectedIds}
                sortModel={translationSortModel}
                setSortModel={(model: any) => {
                  setTranslationSortModel(model);
                  setTranslationPaginationModel((prev: any) => ({ ...prev, page: 0 }));
                }}
                sortingMode="server"
                filterColumns={translationFilterColumns}
                onFilterApply={(filterState: Record<string, any>) => {
                  setTranslationFilters(filterState);
                  setTranslationPaginationModel((prev: any) => ({ ...prev, page: 0 }));
                }}
                onFilterReset={() => {
                  setTranslationFilters(null);
                  setTranslationPaginationModel((prev: any) => ({ ...prev, page: 0, search: '' }));
                }}
                activeFilters={translationFilters}
                onRefresh={refetch}
                checkboxSelection={false}
              />
            )}
          </Stack>
        </Paper>

        <CtaDictionaryModal
          open={openCtaModal}
          handleClose={() => setOpenCtaModal(false)}
          editingItem={editingItem}
          onSaved={refetch}
        />

        <CtaLanguageModal
          open={openLanguageModal}
          handleClose={() => setOpenLanguageModal(false)}
          editingLanguage={editingLanguage}
          onSaved={refetch}
        />

        <CtaTranslationModal
          open={openTranslationModal}
          handleClose={() => setOpenTranslationModal(false)}
          translationRow={editingTranslation}
          onSaved={refetch}
        />
      </Stack>
    </Dashboard>
  );
};
